import { ICheckinType } from '../../../journal/model/ICheckin';
import { ICareMapSection } from '../../../caremap/ICareMap';
import { CustomOrgTemplates } from './CustomOrgTemplates';

export interface INoteTemplate {
    templateId: string;
    label: string;
    body: string;
    target: INoteTemplateTarget;
    focusLocation?: number;
    useLabelForTitle?: boolean;
};

export interface ICareMapNoteTemplateTarget {
    section: ICareMapSection;
    forMember?: boolean;
};

export interface ICheckinNoteTemplateTarget {
    section: 'checkin';
    checkinType?: ICheckinType;
};

export type INoteTemplateTarget = ICareMapNoteTemplateTarget | ICheckinNoteTemplateTarget;

type ITemplateId =
    // checkins
    'gratitude' |
    'mindful-reflection' |
    'self-reflection' |
    'affirmation' |
    'daily-snapshot' |
    'progress-achievements' |
    'health-overview' |
    'special-moments-connections'
;

const templateBodyById: { [key in ITemplateId]: string } = {
    // checkins
    'gratitude': `
🙏 Gratitude

List three things you are grateful for today:

1.\xA0`,

    'mindful-reflection': `
🌅 Mindful Reflection

Describe a moment today when you felt completely present. What were you doing, and how did it make you feel?
➡️\xA0`,

    'self-reflection': `
🤔 Self-Reflection

How am I feeling right now?
➡️\xA0

What makes me hopeful?
➡️\xA0

What makes me worried?
➡️\xA0

What can I accept that I cannot change?
➡️\xA0`,

    'affirmation': `
🙌 Affirmation

Write down three positive things about yourself or your caregiving today.
How do these positive thoughts make you feel and change your outlook?

1.\xA0`,

    'daily-snapshot': `
📸 Daily Snapshot

Record an overview of the day’s caregiving.

Caregiving Observations:
➡️\xA0

Recipient’s Mood/Behavior:
➡️\xA0

Highlights of the Day:
➡️\xA0

Concerns or Challenges:
➡️\xA0`,

    'progress-achievements': `
🏆 Progress & Achievements

Celebrate and document important milestones.

Milestone Achieved:
➡️\xA0

Additional Context:
➡️\xA0

Next Steps or Follow-Up Goals:
➡️\xA0`,

    'health-overview': `
🩺 Health Overview

Log observations specific to care recipient health.

Vital Signs/Health Observations:
➡️\xA0

Symptoms Noted:
➡️\xA0

Action Items or Adjustments:
➡️\xA0`,

    'special-moments-connections': `
✨ Special Moments & Connections

Capture meaningful moments, conversations, or interactions with the care recipient or visitors.

Memorable Interaction:
➡️\xA0

Conversation Highlights:
➡️\xA0

Visitor Details:
➡️\xA0

Impact on Recipient’s Day:
➡️\xA0`
};

const templateLabelById: { [key in ITemplateId]: string } = {
    // checkins
    'gratitude': '🙏 Gratitude',
    'mindful-reflection': '🌅 Mindful Reflection',
    'self-reflection': '🤔 Self-Reflection',
    'affirmation': '🙌 Affirmation',
    'daily-snapshot': '📸 Daily Snapshot',
    'progress-achievements': '🏆 Progress & Achievements',
    'health-overview': '🩺 Health Overview',
    'special-moments-connections': '✨ Special Moments & Connections'
};

const templateFocusLocationById: { [key in ITemplateId]: number | undefined } = {
    // checkins
    'gratitude': undefined,
    'mindful-reflection': undefined,
    'self-reflection': 51,
    'affirmation': undefined,
    'daily-snapshot': 92,
    'progress-achievements': 97,
    'health-overview': 109,
    'special-moments-connections': 156
};

const templateLabelForTitleById: { [key in ITemplateId]?: boolean | undefined } = {

};

const templateIdsByTarget: { [target: string]: ITemplateId[] } = {
    'patient': [],

    'household/member': [],
    'household': [],

    // checkins
    'checkin/personal': [
        'gratitude',
        'mindful-reflection',
        'self-reflection',
        'affirmation'
    ],
    'checkin/care': [
        'daily-snapshot',
        'progress-achievements',
        'health-overview',
        'special-moments-connections'
    ]
};

export class TemplatesConfig {
    static inject = () => [CustomOrgTemplates];

    constructor(orgTemplates: CustomOrgTemplates){
        this.templates = [
            ...this.buildNormalTemplates(),
            ...orgTemplates.getTemplates()
        ];
    }

    templates: INoteTemplate[];

    private buildNormalTemplates = (): INoteTemplate[] => {
        const templates:  INoteTemplate[] = [];
        for (const targetString of Object.keys(templateIdsByTarget)){
            const [section, subsection] = targetString.split('/');

            let target: INoteTemplateTarget;

            if (section === 'checkin') {
                target = {
                    section,
                    checkinType: subsection as ICheckinType,
                };
            } else {
                target = {
                    section: section as ICareMapSection,
                    forMember: subsection === 'member'
                };
            }

            for (const templateId of templateIdsByTarget[targetString]){
                const body = templateBodyById[templateId];
                const label = templateLabelById[templateId];
                const focusLocation = templateFocusLocationById[templateId];
                const useLabelForTitle = templateLabelForTitleById[templateId];
                templates.push({
                    templateId,
                    label,
                    body,
                    target,
                    focusLocation,
                    useLabelForTitle
                });
            }
        }

        return templates;
    };
};
